import { httpGET } from '../../services/http';
import type { PresignedUrlResponse } from '../../types/file';
import type { Lamp } from '../../types/lamp';
import type { IesCandelaGrid } from '../../types/lampWireframes';
import type { Light } from '../../types/light';
import { fetchLdcAndBuildWireframeWithGrid } from '../../util/ldc';

export interface LuminairePlotSource {
  uuid: string;
  name: string;
  distanceToGround: number;
  iesFileId?: number;
  productId?: number;
  variantId?: number;
}

const variantGridCache = new Map<number, IesCandelaGrid | null>();
const productCache = new Map<number, { variants?: Array<{ id: string | number; ldcFileUrl?: string }> } | null>();
const iesFileGridCache = new Map<number, IesCandelaGrid>();

async function loadVariantGrid(productId: number, variantId: number): Promise<IesCandelaGrid | undefined> {
  if (variantGridCache.has(variantId)) {
    const cached = variantGridCache.get(variantId);
    return cached ?? undefined;
  }

  let grid: IesCandelaGrid | null = null;
  try {
    let product = productCache.get(productId);
    if (product === undefined) {
      const resp = await httpGET(`/catalogue/products/${productId}`);
      product = (resp.data as { variants?: Array<{ id: string | number; ldcFileUrl?: string }> }) ?? null;
      productCache.set(productId, product);
    }
    const variant = product?.variants?.find((v) => v.id === variantId || String(v.id) === String(variantId));
    if (variant?.ldcFileUrl) {
      const { grid: parsed } = await fetchLdcAndBuildWireframeWithGrid(variant.ldcFileUrl);
      grid = parsed;
    }
  } catch (err) {
    console.warn('[pdf-plots] failed to load variant LDC', { productId, variantId }, err);
  }

  variantGridCache.set(variantId, grid);
  return grid ?? undefined;
}

async function loadIesFileGrid(iesFileId: number): Promise<IesCandelaGrid | undefined> {
  const cached = iesFileGridCache.get(iesFileId);
  if (cached) return cached;

  try {
    const resp = await httpGET(`/files/${iesFileId}/presigned-url/download`);
    const url = (resp.data as PresignedUrlResponse).url;
    const { grid } = await fetchLdcAndBuildWireframeWithGrid(url);
    iesFileGridCache.set(iesFileId, grid);
    return grid;
  } catch (err) {
    console.warn('[pdf-plots] failed to load IES file', iesFileId, err);
    return undefined;
  }
}

/** Resolve IES candela grids for PDF LDC plots (mirrors FrontendMeasurementRunner loading). */
export async function resolveLuminaireIesGrids(
  sources: LuminairePlotSource[],
  getWireframeGrid: (lampUuid: string) => IesCandelaGrid | undefined,
): Promise<Map<string, IesCandelaGrid>> {
  const entries = await Promise.all(
    sources.map(async (source) => {
      let grid = getWireframeGrid(source.uuid);
      if (!grid && source.productId != null && source.variantId != null) {
        grid = await loadVariantGrid(source.productId, source.variantId);
      }
      if (!grid && source.iesFileId != null) {
        grid = await loadIesFileGrid(source.iesFileId);
      }
      return grid ? ([source.uuid, grid] as const) : null;
    }),
  );

  const result = new Map<string, IesCandelaGrid>();
  for (const entry of entries) {
    if (entry) result.set(entry[0], entry[1]);
  }
  return result;
}

/** Warm the LDC/IES cache while frontend measurement is still running. */
export function prefetchLuminaireIesGrids(
  sources: LuminairePlotSource[],
  getWireframeGrid: (lampUuid: string) => IesCandelaGrid | undefined,
): void {
  void resolveLuminaireIesGrids(sources, getWireframeGrid);
}

export function buildLuminairePlotSources(
  workspaceLamps: Lamp[],
  versionLights: Light[] | undefined,
): LuminairePlotSource[] {
  const lightsByUuid = new Map((versionLights ?? []).map((l) => [l.uuid, l]));

  const sources: LuminairePlotSource[] =
    workspaceLamps.length > 0
      ? workspaceLamps.map((lamp) => {
          const versionLight = lightsByUuid.get(lamp.uuid);
          const distToGround =
            lamp.distanceToGround != null && lamp.distanceToGround > 0
              ? lamp.distanceToGround
              : (versionLight?.distanceToGround ?? 8);
          return {
            uuid: lamp.uuid,
            name: lamp.name,
            distanceToGround: distToGround,
            iesFileId: lamp.iesFileId ?? versionLight?.iesFileId,
            productId: lamp.productId ?? versionLight?.productId,
            variantId: lamp.variantId ?? versionLight?.variantId,
          };
        })
      : (versionLights ?? []).map((light) => ({
          uuid: light.uuid,
          name: light.name,
          distanceToGround: light.distanceToGround > 0 ? light.distanceToGround : 8,
          iesFileId: light.iesFileId,
          productId: light.productId,
          variantId: light.variantId,
        }));

  return sources;
}

/** Cache/signature key for plot inputs — includes mounting height (iso-illuminance). */
export function luminairePlotSourceCacheKey(sources: LuminairePlotSource[]): string {
  return sources
    .map(
      (s) =>
        `${s.uuid}:${s.productId ?? ''}:${s.variantId ?? ''}:${s.iesFileId ?? ''}:${s.distanceToGround.toFixed(2)}`,
    )
    .sort()
    .join('|');
}

export function buildLuminairePlotSignature(workspaceLamps: Lamp[], versionLights: Light[] | undefined): string {
  return luminairePlotSourceCacheKey(buildLuminairePlotSources(workspaceLamps, versionLights));
}
